/**
 * v1.14.63.0 — D&O Multi-Company Questionnaire — Admin form
 *
 * L'operatore compila UN form unificato (campi di tutte le compagnie),
 * sceglie quali compagnie includere, e il sistema genera N PDF compilati.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Download, Loader2, FileText, Building2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/do`;

export default function DOQuestionnaireFormPage() {
  const [templates, setTemplates] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedCompagnie, setSelectedCompagnie] = useState([]);
  const [qid, setQid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [compiledPdfs, setCompiledPdfs] = useState([]);

  useEffect(() => {
    axios.get(`${BASE}/templates`, { withCredentials: true })
      .then((r) => {
        setTemplates(r.data);
        setSelectedCompagnie(Object.keys(r.data.compagnie));
      })
      .catch(() => toast.error("Impossibile caricare i template D&O"));
  }, []);

  const setField = (sezione, campo, value) => {
    setFormData((prev) => ({
      ...prev,
      [sezione]: { ...(prev[sezione] || {}), [campo]: value },
    }));
  };

  const getField = (sezione, campo) =>
    (formData[sezione] || {})[campo] ?? "";

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (!qid) {
        const { data } = await axios.post(`${BASE}/questionnaires`, {
          form_data: formData,
          compagnie_target: selectedCompagnie,
        }, { withCredentials: true });
        setQid(data.id);
        toast.success(`Bozza salvata (ID ${data.id.slice(0, 8)})`);
      } else {
        await axios.put(`${BASE}/questionnaires/${qid}`, {
          form_data: formData,
          compagnie_target: selectedCompagnie,
        }, { withCredentials: true });
        toast.success("Bozza aggiornata");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const compile = async () => {
    if (selectedCompagnie.length === 0) {
      toast.error("Seleziona almeno una compagnia");
      return;
    }
    setCompiling(true);
    try {
      let id = qid;
      if (!id) {
        const { data } = await axios.post(`${BASE}/questionnaires`, {
          form_data: formData,
          compagnie_target: selectedCompagnie,
        }, { withCredentials: true });
        id = data.id;
        setQid(id);
      } else {
        await axios.put(`${BASE}/questionnaires/${id}`, {
          form_data: formData,
          compagnie_target: selectedCompagnie,
        }, { withCredentials: true });
      }
      const { data } = await axios.post(
        `${BASE}/questionnaires/${id}/compile`,
        { compagnie: selectedCompagnie },
        { withCredentials: true },
      );
      const compiled = data.compiled || [];
      setCompiledPdfs(compiled);
      const ok = compiled.filter((p) => !p.error).length;
      const ko = compiled.length - ok;
      if (ko === 0) {
        toast.success(`Generati ${ok} PDF`);
      } else if (ok > 0) {
        toast.warning(`Generati ${ok}/${compiled.length} PDF · ${ko} errori`);
      } else {
        toast.error("Nessun PDF generato (errori su tutte le compagnie)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || "Errore compilazione");
    } finally {
      setCompiling(false);
    }
  };

  const toggleCompagnia = (slug) => {
    setSelectedCompagnie((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug)
                          : [...prev, slug],
    );
  };

  if (!templates) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto p-4" data-testid="do-questionnaire-page">
      {/* FORM (col 1-2) */}
      <div className="lg:col-span-2 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#163321]">
              <FileText className="w-5 h-5" />
              Questionario assuntivo D&amp;O — Multi-compagnia
            </CardTitle>
            <p className="text-sm text-slate-500">
              Compila UN solo form, il sistema genera i PDF specifici delle compagnie selezionate.
              {templates.campi_totali_unificati} campi unificati totali.
            </p>
          </CardHeader>
        </Card>

        {Object.entries(templates.sezioni).map(([sezKey, sez]) => (
          <Card key={sezKey} data-testid={`do-section-${sezKey}`}>
            <CardHeader>
              <CardTitle className="text-base text-[#163321] bg-[#F0E7C8] px-3 py-1.5 rounded">
                {sez.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(sez.campi).map(([campoKey, campo]) => {
                const val = getField(sezKey, campoKey);
                const tid = `do-field-${sezKey}-${campoKey}`;
                if (campo.type === "boolean") {
                  return (
                    <label key={campoKey} className="flex items-start gap-2 col-span-1 md:col-span-2 cursor-pointer">
                      <Checkbox
                        data-testid={tid}
                        checked={!!val}
                        onCheckedChange={(c) => setField(sezKey, campoKey, !!c)}
                      />
                      <span className="text-sm">{campo.label}{campo.required && <span className="text-red-500"> *</span>}</span>
                    </label>
                  );
                }
                if (campo.type === "textarea") {
                  return (
                    <div key={campoKey} className="col-span-1 md:col-span-2">
                      <Label className="text-xs">{campo.label}{campo.required && <span className="text-red-500"> *</span>}</Label>
                      <Textarea
                        data-testid={tid}
                        value={val}
                        onChange={(e) => setField(sezKey, campoKey, e.target.value)}
                        rows={3}
                      />
                    </div>
                  );
                }
                return (
                  <div key={campoKey}>
                    <Label className="text-xs">{campo.label}{campo.required && <span className="text-red-500"> *</span>}</Label>
                    <Input
                      data-testid={tid}
                      type={campo.type === "date" ? "date"
                             : campo.type === "numeric" || campo.type === "currency" ? "number"
                             : "text"}
                      value={val}
                      onChange={(e) => setField(sezKey, campoKey, e.target.value)}
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* SIDEBAR — compagnie + azioni */}
      <div className="space-y-4">
        <Card className="sticky top-4" data-testid="do-sidebar">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-[#163321]">
              <Building2 className="w-4 h-4" /> Compagnie target
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(templates.compagnie).map(([slug, c]) => (
              <label key={slug} data-testid={`do-compagnia-toggle-${slug}`}
                     className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                <Checkbox
                  checked={selectedCompagnie.includes(slug)}
                  onCheckedChange={() => toggleCompagnia(slug)}
                />
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#163321]">{c.name}</p>
                  <p className="text-xs text-slate-500">
                    {c.campi_supportati.length} campi · {c.strategy === "pypdf_form_fields" ? "PDF interattivo" : "Scheda riepilogativa"}
                  </p>
                </div>
              </label>
            ))}

            <div className="pt-3 space-y-2 border-t">
              <Button
                onClick={saveDraft}
                disabled={saving}
                variant="outline"
                className="w-full"
                data-testid="do-btn-save-draft"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva bozza"}
              </Button>
              <Button
                onClick={compile}
                disabled={compiling || selectedCompagnie.length === 0}
                className="w-full bg-[#163321] hover:bg-[#1d4a2e] text-white"
                data-testid="do-btn-compile"
              >
                {compiling ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generazione...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Genera {selectedCompagnie.length} questionari
                  </span>
                )}
              </Button>
            </div>

            {compiledPdfs.length > 0 && (
              <div className="pt-3 border-t space-y-2" data-testid="do-compiled-list">
                <p className="text-xs font-bold text-[#163321]">PDF generati:</p>
                {compiledPdfs.map((p) => (
                  <a
                    key={p.company_slug}
                    href={`${process.env.REACT_APP_BACKEND_URL}${p.download_url}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`do-pdf-dl-${p.company_slug}`}
                    className="flex items-center gap-2 p-2 rounded bg-amber-50 hover:bg-amber-100 text-sm text-[#163321]"
                  >
                    <Download className="w-4 h-4" />
                    {p.error ? `❌ ${p.company_name || p.company_slug}: ${p.error.slice(0, 40)}` : (
                      <>
                        <span className="flex-1">{p.company_name}</span>
                        <span className="text-xs text-slate-500">{Math.round(p.size_bytes / 1024)}KB</span>
                      </>
                    )}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
